import { batchProxy } from '@/utils/batch-proxy';
import { create } from '@patsnap/tahiti-core';
import _ from 'lodash';

export class BatchLoader<T, R> {
    private _batchFn: (ids: string[]) => Promise<R>;
    private _cache: Map<string, Promise<T>> = new Map();

    constructor(
        private readonly fetchData: (ids: string[]) => Promise<R>,
        private readonly transformData: (id: string, data: R) => T,
        batchDelay = 500,
    ) {
        this._batchFn = batchProxy((ids: string[]) => {
            const uniqueIds = _.uniq(ids);
            return this.fetchData(uniqueIds);
        }, batchDelay);
    }

    async load(id: string): Promise<T> {
        if (this._cache.has(id)) {
            return this._cache.get(id)!;
        }

        const deferred = create<T>();
        this._cache.set(id, deferred.promise);

        const data = await this._batchFn([id]);
        const result = this.transformData(id, data);

        deferred.resolve(result);
        return result;
    }

    /**
     * 批量加载, 如果ids中存在已有数据的id, 则不重复加载
     * 类似DataLoader的loadMany方法，充分利用缓存
     * @param ids 需要加载的ID数组
     * @returns 按照请求的ID顺序返回结果数组
     * @author cursor
     */
    async loadMany(ids: string[]): Promise<T[]> {
        if (ids.length === 0) {
            return [];
        }

        // 对ID进行去重
        const uniqueIds = _.uniq(ids);

        // 分离已缓存和未缓存的ID
        const cachedIds: string[] = [];
        const uncachedIds: string[] = [];

        uniqueIds.forEach(id => {
            if (this._cache.has(id)) {
                cachedIds.push(id);
            } else {
                uncachedIds.push(id);
            }
        });

        // 为未缓存的ID创建延迟对象并添加到缓存
        const deferreds: Map<string, { resolve: (value: T) => void }> = new Map();

        uncachedIds.forEach(id => {
            const deferred = create<T>();
            this._cache.set(id, deferred.promise);
            deferreds.set(id, deferred);
        });

        // 如果有未缓存的ID，进行批量加载
        if (uncachedIds.length > 0) {
            const data = await this._batchFn(uncachedIds);

            // 解析每个未缓存ID的延迟对象
            uncachedIds.forEach(id => {
                const result = this.transformData(id, data);
                const deferred = deferreds.get(id)!;
                deferred.resolve(result);
            });
        }

        // 按原始ID顺序返回结果
        return Promise.all(ids.map(id => this._cache.get(id)!));
    }
}
